import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setNotifications,
  addNotification,
  markAsRead,
  markAllAsRead,
  deleteNotification
} from '../../../redux/slices/NotificationSlice';
import { useTranslation } from 'react-i18next';
import CustomChip from '../../../shared/CustomChip';
import { InputSwitch } from 'primereact/inputswitch';
import DropdownField from '../../../shared/DropdownField';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { Checkbox } from 'primereact/checkbox';
import Button from '../../../shared/CustomButton';
import { 
  FaBell, 
  FaBellSlash, 
  FaCog, 
  FaCheck, 
  FaTrash, 
  FaClock,
  FaExclamationTriangle,
  FaInfoCircle,
  FaCheckCircle,
  FaFilter,
  FaUserCheck,
  FaEnvelope
} from 'react-icons/fa';
import { helpDeskDepartments } from '../../../constants/QueueManagementConstants';

export default function NotificationSystemTab() {
  const { t } = useTranslation();
  
  const dispatch = useDispatch();
  const notifications = useSelector(state => state.notification.notifications);

  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const [preferences, setPreferences] = useState({
    email: {
      enabled: true,
      ticketAssigned: true,
      statusChange: false,
      newComments: true,
      criticalTickets: true,
      overdueTickets: true,
      dailySummary: true,
      frequency: 'immediate'
    },
    inApp: {
      enabled: true,
      ticketAssigned: true,
      statusChange: true,
      newComments: true,
      criticalTickets: true,
      overdueTickets: true,
      sound: true
    },
    departments: {
      IT: true,
      HR: true,
      Admin: false,
      Finance: true,
      SupplyChain: false
    }
  });

  const [filters, setFilters] = useState({
    type: '',
    department: '',
    priority: '',
    isRead: '',
    requiresAction: ''
  });

  // Apply filters
  useEffect(() => {
    let filtered = [...(notifications || [])];
    if (filters.type) {
      filtered = filtered.filter(n => n.type === filters.type);
    }
    if (filters.department) {
      filtered = filtered.filter(n => n.department === filters.department);
    }
    if (filters.priority) {
      filtered = filtered.filter(n => n.priority === filters.priority);
    }
    if (filters.isRead !== '') {
      const readStatus = filters.isRead === 'true';
      filtered = filtered.filter(n => n.isRead === readStatus);
    }
    if (filters.requiresAction !== '') {
      const actionStatus = filters.requiresAction === 'true';
      filtered = filtered.filter(n => n.requiresAction === actionStatus);
    }
    setFilteredNotifications(filtered);
  }, [filters, notifications]);

  const handleMarkAsRead = (notificationId) => {
    dispatch(markAsRead(notificationId));
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsRead());
  };

  const handleDeleteNotification = (notificationId) => {
    dispatch(deleteNotification(notificationId));
  };

  const handlePreferenceChange = (category, key, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'assignment': return <FaUserCheck className="text-blue-500" />;
      case 'status_change': return <FaInfoCircle className="text-purple-500" />;
      case 'critical': return <FaExclamationTriangle className="text-red-500" />;
      case 'comment': return <FaEnvelope className="text-green-500" />;
      case 'overdue': return <FaClock className="text-orange-500" />;
      case 'created': return <FaCheckCircle className="text-teal-500" />;
      default: return <FaBell className="text-gray-500" />;
    }
  };

  const getPriorityBadge = (priority) => {
    let chipType = 'secondary';
    if (priority === 'Critical') chipType = 'danger';
    else if (priority === 'High') chipType = 'warning';
    else if (priority === 'Medium') chipType = 'info';
    else if (priority === 'Low') chipType = 'success';
    return (
      <CustomChip label={priority} chipType={chipType} size="sm" />
    );
  };

  const getNotificationTypeOptions = () => [
    { label: t('All Types'), value: '' },
    { label: t('Assignment'), value: 'assignment' },
    { label: t('Status Change'), value: 'status_change' },
    { label: t('Critical'), value: 'critical' },
    { label: t('Comment'), value: 'comment' },
    { label: t('Overdue'), value: 'overdue' },
    { label: t('Created'), value: 'created' }
  ];

  const getPriorityOptions = () => [
    { label: t('All Priorities'), value: '' },
    { label: t('Critical'), value: 'Critical' },
    { label: t('High'), value: 'High' },
    { label: t('Medium'), value: 'Medium' },
    { label: t('Low'), value: 'Low' }
  ];

  const getReadStatusOptions = () => [
    { label: t('All'), value: '' },
    { label: t('Read'), value: 'true' },
    { label: t('Unread'), value: 'false' }
  ];

  const getActionOptions = () => [
    { label: t('All'), value: '' },
    { label: t('Requires Action'), value: 'true' },
    { label: t('No Action'), value: 'false' }
  ];

  // Statistics
  const totalNotifications = notifications?.length || 0;
  const unreadCount = notifications?.filter(n => !n.isRead).length || 0;
  const actionRequiredCount = notifications?.filter(n => n.requiresAction && !n.isRead).length || 0;
  const criticalCount = notifications?.filter(n => n.priority === 'Critical' && !n.isRead).length || 0;

  return (
    <div className="p-4">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FaBell className="text-2xl text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {t("Notification Center")}
                </h1>
                <p className="text-gray-600 text-sm mt-1">
                  {t("Stay updated on ticket activities and manage your notification preferences")}
                </p>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button
                onClick={handleMarkAllAsRead}
                label={t("Mark All Read")}
                icon={<FaCheck />}
                buttonType="secondary"
                size="small"
              />
            </div>
          </div>

          {/* Statistics */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center border border-blue-200">
              <div className="text-2xl font-bold text-blue-600">{totalNotifications}</div>
              <div className="text-sm text-blue-700">{t("Total")}</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center border border-yellow-200">
              <div className="text-2xl font-bold text-yellow-600">{unreadCount}</div>
              <div className="text-sm text-yellow-700">{t("Unread")}</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg text-center border border-orange-200">
              <div className="text-2xl font-bold text-orange-600">{actionRequiredCount}</div>
              <div className="text-sm text-orange-700">{t("Action Required")}</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center border border-red-200">
              <div className="text-2xl font-bold text-red-600">{criticalCount}</div>
              <div className="text-sm text-red-700">{t("Critical")}</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6">
          <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
            {/* Notifications Tab */}
            <TabPanel header={`${t('Notifications')} (${unreadCount})`}>
              {/* Filters */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <FaFilter className="text-gray-600" />
                  <h3 className="font-medium text-gray-900">{t('Filter Notifications')}</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">
                      {t("Type")}
                    </label>
                    <DropdownField
                      value={filters.type}
                      handleChange={(e) => handleFilterChange('type', e.value)}
                      options={getNotificationTypeOptions()}
                      placeholder={t("All Types")}
                      useFormik={false}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">
                      {t("Department")}
                    </label>
                    <DropdownField
                      value={filters.department}
                      handleChange={(e) => handleFilterChange('department', e.value)}
                      options={[{ label: t('All Departments'), value: '' }, ...helpDeskDepartments]}
                      placeholder={t("All Departments")}
                      useFormik={false}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">
                      {t("Priority")}
                    </label>
                    <DropdownField
                      value={filters.priority}
                      handleChange={(e) => handleFilterChange('priority', e.value)}
                      options={getPriorityOptions()}
                      placeholder={t("All Priorities")}
                      useFormik={false}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">
                      {t("Read Status")}
                    </label>
                    <DropdownField
                      value={filters.isRead}
                      handleChange={(e) => handleFilterChange('isRead', e.value)}
                      options={getReadStatusOptions()}
                      placeholder={t("All")}
                      useFormik={false}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">
                      {t("Action")}
                    </label>
                    <DropdownField
                      value={filters.requiresAction}
                      handleChange={(e) => handleFilterChange('requiresAction', e.value)}
                      options={getActionOptions()}
                      placeholder={t("All")}
                      useFormik={false}
                    />
                  </div>
                </div>
              </div>

              {/* Notifications Timeline */}
              <div className="space-y-4">
                {filteredNotifications && filteredNotifications.length > 0 ? (
                  filteredNotifications.map((notification) => (
                    <Card key={notification.id} className={`${!notification.isRead ? 'border-l-4 border-l-blue-500 bg-blue-50' : ''}`}>
                      <div className="flex items-start justify-between p-4">
                        <div className="flex items-start gap-4 flex-1">
                          {getNotificationIcon(notification.type)}
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className={`font-medium ${!notification.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                                {notification.title}
                              </h4>
                              {getPriorityBadge(notification.priority)}
                              {notification.requiresAction && (
                                <CustomChip label={t("Action Required")}
                                  chipType="warning" size="sm" />
                              )}
                              {!notification.isRead && (
                                <CustomChip label={t("New")} chipType="info" size="sm" />
                              )}
                            </div>
                            
                            <p className="text-gray-600 mb-2">{notification.message}</p>
                            
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>{t("Department")}: {notification.department}</span>
                              <span>{t("Ticket")}: {notification.ticketId}</span>
                              <span>{t("Assignee")}: {notification.assignee}</span>
                              <span>{new Date(notification.timestamp).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 ml-4">
                          {!notification.isRead && (
                            <Button
                              onClick={() => handleMarkAsRead(notification.id)}
                              icon={<FaCheck />}
                              buttonType="success"
                              size="small"
                              tooltip={t('Mark as Read')}
                            />
                          )}
                          <Button
                            onClick={() => handleDeleteNotification(notification.id)}
                            icon={<FaTrash />}
                            buttonType="danger"
                            size="small"
                            tooltip={t('Delete')}
                          />
                        </div>
                      </div>
                    </Card>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <FaBellSlash className="text-4xl text-gray-400 mb-4 mx-auto" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {t("No notifications found")}
                    </h3>
                    <p className="text-gray-600">
                      {t("Try adjusting your filters or check back later.")}
                    </p>
                  </div>
                )}
              </div>
            </TabPanel>

            {/* Preferences Tab */}
            <TabPanel header={t('Preferences')}>
              <div className="max-w-4xl">
                <div className="flex items-center gap-2 mb-6">
                  <FaCog className="text-gray-600" />
                  <h3 className="font-medium text-gray-900">{t('Notification Preferences')}</h3>
                </div>

                {/* Email Preferences */}
                <Card className="mb-6">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium flex items-center gap-2">
                        <FaEnvelope className="text-blue-600" />
                        {t("Email Notifications")}
                      </h4>
                      <InputSwitch
                        checked={preferences.email.enabled}
                        onChange={(e) => handlePreferenceChange('email', 'enabled', e.value)}
                      />
                    </div>
                    
                    {preferences.email.enabled && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex items-center justify-between">
                            <span>{t("Ticket Assigned")}</span>
                            <InputSwitch
                              checked={preferences.email.ticketAssigned}
                              onChange={(e) => handlePreferenceChange('email', 'ticketAssigned', e.value)}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <span>{t("Status Changes")}</span>
                            <InputSwitch
                              checked={preferences.email.statusChange}
                              onChange={(e) => handlePreferenceChange('email', 'statusChange', e.value)}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <span>{t("New Comments")}</span>
                            <InputSwitch
                              checked={preferences.email.newComments}
                              onChange={(e) => handlePreferenceChange('email', 'newComments', e.value)}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <span>{t("Critical Tickets")}</span>
                            <InputSwitch
                              checked={preferences.email.criticalTickets}
                              onChange={(e) => handlePreferenceChange('email', 'criticalTickets', e.value)}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <span>{t("Overdue Tickets")}</span>
                            <InputSwitch
                              checked={preferences.email.overdueTickets}
                              onChange={(e) => handlePreferenceChange('email', 'overdueTickets', e.value)}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <span>{t("Daily Summary")}</span>
                            <InputSwitch
                              checked={preferences.email.dailySummary}
                              onChange={(e) => handlePreferenceChange('email', 'dailySummary', e.value)}
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-gray-700 block mb-2">
                            {t("Email Frequency")}
                          </label>
                          <DropdownField
                            value={preferences.email.frequency}
                            handleChange={(e) => handlePreferenceChange('email', 'frequency', e.value)}
                            options={[
                              { label: t('Immediate'), value: 'immediate' },
                              { label: t('Hourly'), value: 'hourly' },
                              { label: t('Daily'), value: 'daily' },
                              { label: t('Weekly'), value: 'weekly' }
                            ]}
                            placeholder={t('Immediate')}
                            useFormik={false}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </Card>

                {/* In-App Preferences */}
                <Card className="mb-6">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium flex items-center gap-2">
                        <FaBell className="text-green-600" />
                        {t("In-App Notifications")}
                      </h4>
                      <InputSwitch
                        checked={preferences.inApp.enabled}
                        onChange={(e) => handlePreferenceChange('inApp', 'enabled', e.value)}
                      />
                    </div>
                    
                    {preferences.inApp.enabled && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center justify-between">
                          <span>{t("Ticket Assigned")}</span>
                          <InputSwitch
                            checked={preferences.inApp.ticketAssigned}
                            onChange={(e) => handlePreferenceChange('inApp', 'ticketAssigned', e.value)}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span>{t("Status Changes")}</span>
                          <InputSwitch
                            checked={preferences.inApp.statusChange}
                            onChange={(e) => handlePreferenceChange('inApp', 'statusChange', e.value)}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span>{t("New Comments")}</span>
                          <InputSwitch
                            checked={preferences.inApp.newComments}
                            onChange={(e) => handlePreferenceChange('inApp', 'newComments', e.value)}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span>{t("Critical Tickets")}</span>
                          <InputSwitch
                            checked={preferences.inApp.criticalTickets}
                            onChange={(e) => handlePreferenceChange('inApp', 'criticalTickets', e.value)}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span>{t("Overdue Tickets")}</span>
                          <InputSwitch
                            checked={preferences.inApp.overdueTickets}
                            onChange={(e) => handlePreferenceChange('inApp', 'overdueTickets', e.value)}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span>{t("Sound Alerts")}</span>
                          <InputSwitch
                            checked={preferences.inApp.sound}
                            onChange={(e) => handlePreferenceChange('inApp', 'sound', e.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Department Subscriptions */}
                <Card>
                  <div className="p-4">
                    <h4 className="text-lg font-medium mb-4">
                      {t("Department Subscriptions")}
                    </h4>
                    <p className="text-gray-600 mb-4">
                      {t("Choose which departments you want to receive notifications from")}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {helpDeskDepartments.map((dept) => (
                        <div key={dept.value} className="flex items-center gap-2">
                          <Checkbox
                            inputId={dept.value}
                            checked={preferences.departments[dept.value] || false}
                            onChange={(e) => handlePreferenceChange('departments', dept.value, e.checked)}
                          />
                          <label htmlFor={dept.value} className="cursor-pointer">
                            {dept.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </div>
            </TabPanel>
          </TabView>
        </div>
      </div>
    </div>
  );
}